import {useCallback, useRef, useState} from 'react';

import {parseSpreadsheet} from 'bulk-import/SpreadsheetBulkImportDAO';
import * as ImportUtil from 'util/ImportUtil';
import {getUserMessage} from 'util/Net';

function extractErrorMessage(error: unknown): string | null {
  // getUserMessage handles jqXHR and task errors (server responses)
  const serverMessage = getUserMessage(error);
  if (serverMessage != null) {
    return serverMessage;
  }
  // ImportUtil.upload rejects with plain {userMessage: ...} for client-side errors (e.g. file too large)
  const userMessage = (error as {userMessage?: string} | null)?.userMessage;
  if (userMessage != null) {
    return userMessage;
  }
  return null;
}

export interface UseSpreadsheetUploadToS3Options {
  inferDataTypes?: boolean;
}

export interface UseSpreadsheetUploadToS3Result {
  /** S3 URL of the uploaded file, set after successful upload. */
  s3Url: string | null;
  isUploading: boolean;
  /** Parsed column headers from the spreadsheet. Set after upload + parse completes. */
  headers: Array<string> | null;
  /** Total row count from the spreadsheet (includes header row). */
  rowCount: number | null;
  /** Inferred data types per column, when `inferDataTypes` option is enabled. */
  dataTypes: Array<string> | null;
  /** Human-readable error message if upload or header parsing failed. */
  error: string | null;
  /** Raw error response for callers that need richer handling (e.g. notifications.notifyError). */
  errorResponse: unknown | null;
  reset: () => void;
  uploadFile: (file: File) => void;
}

/**
 * Uploads a spreadsheet file to S3 and parses its column headers.
 * Handles CSV/XLS/XLSX files only — SDF and other custom formats
 * should be handled separately by the caller.
 */
export default function useSpreadsheetUploadToS3({
  inferDataTypes,
}: UseSpreadsheetUploadToS3Options = {}): UseSpreadsheetUploadToS3Result {
  const [s3Url, setS3Url] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [headers, setHeaders] = useState<Array<string> | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [dataTypes, setDataTypes] = useState<Array<string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorResponse, setErrorResponse] = useState<unknown | null>(null);
  const uploadIdRef = useRef(0);

  const reset = useCallback(() => {
    uploadIdRef.current += 1;
    setS3Url(null);
    setIsUploading(false);
    setHeaders(null);
    setRowCount(null);
    setDataTypes(null);
    setError(null);
    setErrorResponse(null);
  }, []);

  const uploadFile = useCallback(
    (fileToUpload: File) => {
      uploadIdRef.current += 1;
      const currentUploadId = uploadIdRef.current;

      setIsUploading(true);
      setError(null);
      setErrorResponse(null);
      setS3Url(null);
      setHeaders(null);
      setRowCount(null);
      setDataTypes(null);

      ImportUtil.upload(fileToUpload).then(
        (uploadedS3Url: string) => {
          if (uploadIdRef.current !== currentUploadId) {
            return;
          }
          setS3Url(uploadedS3Url);
          parseSpreadsheet(fileToUpload.name, uploadedS3Url, inferDataTypes).then(
            (result: {columns: Array<string>; row_count: number; data_types?: Array<string>}) => {
              if (uploadIdRef.current !== currentUploadId) {
                return;
              }
              setHeaders(result.columns);
              setRowCount(result.row_count);
              setDataTypes(result.data_types ?? null);
              setIsUploading(false);
            },
            (parseError: unknown) => {
              if (uploadIdRef.current !== currentUploadId) {
                return;
              }
              setS3Url(null);
              setError(extractErrorMessage(parseError) ?? 'Failed to parse spreadsheet headers.');
              setErrorResponse(parseError);
              setIsUploading(false);
            }
          );
        },
        (uploadError: unknown) => {
          if (uploadIdRef.current !== currentUploadId) {
            return;
          }
          setError(extractErrorMessage(uploadError) ?? 'Failed to upload file.');
          setErrorResponse(uploadError);
          setIsUploading(false);
        }
      );
    },
    [inferDataTypes]
  );

  return {
    s3Url,
    isUploading,
    headers,
    rowCount,
    dataTypes,
    error,
    errorResponse,
    reset,
    uploadFile,
  };
}
